import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowRight } from "lucide-react";

export default function SocietyOnboard() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", slug: "", registration_number: "", registration_act: "",
    address: "", contact_phone: "", contact_email: "", pan: "",
    gst_applicable: false, gstin: "", gst_rate: 18, hsn_sac: "",
    unit_count_at_signup: 0,
  });
  const [banks, setBanks] = useState([{ bank_name: "", branch: "", account_number: "", ifsc: "", holder_name: "", purpose: "Maintenance" }]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, bank_accounts: banks.filter((b) => b.bank_name && b.account_number) };
      const { data } = await api.post("/admin/societies", payload);
      toast.success(`${data.name} onboarded.`);
      nav(`/admin/societies/${data.id}`);
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setBusy(false); }
  };

  const setBank = (i, k, v) => setBanks((prev) => prev.map((b, idx) => idx === i ? { ...b, [k]: v } : b));
  const addBank = () => setBanks((p) => [...p, { bank_name: "", branch: "", account_number: "", ifsc: "", holder_name: "", purpose: "General" }]);
  const rmBank = (i) => setBanks((p) => p.filter((_, idx) => idx !== i));

  return (
    <AdminLayout subtitle="Onboarding" title="New Society">
      <form onSubmit={submit} className="space-y-10" data-testid="society-onboard-form">
        <Section title="Society Identity">
          <Grid>
            <Field label="Legal name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="soc-name"/>
            <Field label="URL slug *" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "") })} required testid="soc-slug" hint="lowercase, hyphens only. Used in /portal/<slug>/login"/>
            <Field label="Registration No." value={form.registration_number} onChange={(v) => setForm({ ...form, registration_number: v })} testid="soc-regno"/>
            <Field label="Registration Act" value={form.registration_act} onChange={(v) => setForm({ ...form, registration_act: v })} testid="soc-regact"/>
            <Field label="PAN" value={form.pan} onChange={(v) => setForm({ ...form, pan: v })} testid="soc-pan"/>
            <Field label="Expected unit count" type="number" value={form.unit_count_at_signup} onChange={(v) => setForm({ ...form, unit_count_at_signup: Number(v) })} testid="soc-units"/>
          </Grid>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Registered address</label>
            <textarea rows="2" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid="soc-address"
              className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none"/>
          </div>
          <Grid className="mt-4">
            <Field label="Contact phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testid="soc-phone"/>
            <Field label="Contact email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="soc-email"/>
          </Grid>
        </Section>

        <Section title="GST" desc="Turn on if the society is GST-registered. Invoices will then show tax breakup.">
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.gst_applicable} onChange={(e) => setForm({ ...form, gst_applicable: e.target.checked })} data-testid="soc-gst-toggle"
                className="w-5 h-5 accent-terracotta"/>
              <span className="text-sm font-medium">GST Applicable</span>
            </label>
          </div>
          {form.gst_applicable && (
            <Grid className="mt-4">
              <Field label="GSTIN" value={form.gstin} onChange={(v) => setForm({ ...form, gstin: v })} testid="soc-gstin"/>
              <Field label="GST rate %" type="number" value={form.gst_rate} onChange={(v) => setForm({ ...form, gst_rate: Number(v) })} testid="soc-gstrate"/>
              <Field label="HSN / SAC" value={form.hsn_sac} onChange={(v) => setForm({ ...form, hsn_sac: v })} testid="soc-hsn"/>
            </Grid>
          )}
        </Section>

        <Section title="Bank Accounts" desc="One is fine — used for both maintenance and sinking. Add more to split by purpose.">
          <div className="space-y-3">
            {banks.map((b, i) => (
              <div key={i} className="grid md:grid-cols-6 gap-3 items-end bg-sand-100 border border-stone-200 rounded-xl p-4">
                <Field label="Bank name" value={b.bank_name} onChange={(v) => setBank(i, "bank_name", v)} testid={`bank-name-${i}`}/>
                <Field label="Branch" value={b.branch} onChange={(v) => setBank(i, "branch", v)} testid={`bank-branch-${i}`}/>
                <Field label="A/c number" value={b.account_number} onChange={(v) => setBank(i, "account_number", v)} testid={`bank-acc-${i}`}/>
                <Field label="IFSC" value={b.ifsc} onChange={(v) => setBank(i, "ifsc", v)} testid={`bank-ifsc-${i}`}/>
                <Field label="Holder name" value={b.holder_name} onChange={(v) => setBank(i, "holder_name", v)} testid={`bank-holder-${i}`}/>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Purpose</label>
                  <div className="flex items-center gap-2">
                    <select value={b.purpose} onChange={(e) => setBank(i, "purpose", e.target.value)} data-testid={`bank-purpose-${i}`}
                      className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-3 py-3 text-base focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none">
                      {["Maintenance", "Sinking", "General", "Other"].map((p) => <option key={p}>{p}</option>)}
                    </select>
                    {banks.length > 1 && (
                      <button type="button" onClick={() => rmBank(i)} className="mt-2 p-3 text-stone-500 hover:text-terracotta"><Trash2 size={16}/></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button type="button" onClick={addBank} data-testid="add-bank"
            className="mt-3 inline-flex items-center gap-2 text-sm text-terracotta"><Plus size={14}/> Add another bank account</button>
        </Section>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => nav("/admin")} className="rounded-full border border-stone-300 bg-white px-6 py-3 text-sm font-medium hover:bg-sand-100">Cancel</button>
          <button type="submit" disabled={busy} data-testid="society-onboard-submit"
            className="inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-50 text-white px-8 py-3 text-sm font-medium">
            {busy ? "Creating…" : "Create society"} <ArrowRight size={16}/>
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

function Section({ title, desc, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Section</div>
      <h2 className="editorial text-2xl mb-1">{title}</h2>
      {desc && <p className="text-sm text-stone-500 mb-5">{desc}</p>}
      <div className="mt-4">{children}</div>
    </div>
  );
}
function Grid({ children, className = "" }) { return <div className={`grid md:grid-cols-2 gap-4 ${className}`}>{children}</div>; }

function Field({ label, value, onChange, type = "text", required, testid, hint }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid}
        className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 outline-none"/>
      {hint && <div className="text-xs text-stone-500 mt-1">{hint}</div>}
    </div>
  );
}
